import { useEffect, useState } from 'react'
import type { AddPartsCandidateRow } from './rfq-append-lines'

/** Search debounce (UID-457 item 2, mirroring
 * `verticals/parts/components/AddPartsModal.tsx`'s own `SEARCH_DEBOUNCE_MS`)
 * — without this, every keystroke would fire a real `GET /catalog/search`
 * request. Shared by both add-parts surfaces via this hook. */
const SEARCH_DEBOUNCE_MS = 300

export type AddPartsSearchState =
  | { readonly status: 'loading' }
  | { readonly status: 'error'; readonly message: string }
  | { readonly status: 'ready'; readonly results: readonly AddPartsCandidateRow[] }

export interface UseAddPartsSearchOptions {
  readonly open: boolean
  /** The real, debounced candidate search (`add-parts-search.ts`'s
   * `searchAddPartsCandidates`), injected by the caller — this hook owns
   * debounce/loading/error/abort-on-unmount, not the caller. */
  readonly search: (query: string) => Promise<readonly AddPartsCandidateRow[]>
  /**
   * `AddPartsModal` (compose-time) resets its query on CLOSE — mirroring
   * `verticals/parts/components/AddPartsModal.tsx`'s own reset — so a
   * re-open never flashes the previous visit's stale query/results before
   * the debounced effect below refetches.
   *
   * `AddPartsToRfqModal` (post-submit) resets on OPEN instead, matching the
   * design prototype's `openRfqDetailAdd()` (`rfqDetailAddSearch: ''`) — a
   * stale filter from the previous visit would silently hide most of the
   * list.
   *
   * Both are "reset before this visit's first fetch can be seen," just
   * triggered by opposite edges of `open`.
   */
  readonly resetOn: 'open' | 'close'
}

export interface UseAddPartsSearchResult {
  readonly query: string
  readonly setQuery: (query: string) => void
  readonly state: AddPartsSearchState
}

/**
 * UID-457 item 2 (ADR-0147 D3) — the debounced-search-with-loading/error
 * machinery shared by `AddPartsModal.tsx` (compose-time) and
 * `AddPartsToRfqModal.tsx` (post-submit). Extracted rather than duplicated:
 * the two components' async lifecycle is byte-for-byte identical (debounce,
 * a `cancelled`-guarded fetch, loading/error state) — only the reset EDGE
 * (`resetOn`) and the render markup genuinely differ between them.
 *
 * The `cancelled` guard (not an `AbortController` — `searchAddPartsCandidates`
 * takes no `AbortSignal`) prevents a slow, since-abandoned query's LATE
 * resolution *or* rejection from clobbering a newer query's state — including
 * setting `error` for a query the user has already moved on from.
 */
export function useAddPartsSearch({
  open,
  search,
  resetOn,
}: UseAddPartsSearchOptions): UseAddPartsSearchResult {
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [state, setState] = useState<AddPartsSearchState>({ status: 'loading' })

  useEffect(() => {
    const shouldReset = resetOn === 'close' ? !open : open
    if (shouldReset) {
      setQuery('')
      setDebouncedQuery('')
    }
  }, [open, resetOn])

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [query])

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setState({ status: 'loading' })
    search(debouncedQuery)
      .then((rows) => {
        if (!cancelled) setState({ status: 'ready', results: rows })
      })
      .catch((error: unknown) => {
        if (cancelled) return
        const message = error instanceof Error ? error.message : 'We couldn’t search. Try again.'
        setState({ status: 'error', message })
      })
    return () => {
      cancelled = true
    }
  }, [open, debouncedQuery, search])

  return { query, setQuery, state }
}
